import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { gql, useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { browserOf, nameFromEmail, screenName } from './helpFeedbackContext';
import { HELP_CENTER } from '../../config/helpLinks';

/**
 * Help & feedback (Prototype 2026-10-25): the top bar's "?" opens a small chat
 * panel, bottom right on every screen, where a signed-in person shares
 * feedback or reports a problem. Everything goes to a person on the tinycms
 * team, never Prism AI or another AI. It looks like the reader chat but has
 * its own backend: each message opens a ticket, TC-1042, that staff read in
 * the ops portal and answer by email (apiserver platform/services/supportTickets.js).
 *
 * Who sent it (user, name, email, site) is taken from the session on the
 * server; the app sends only the type, the text and, when the box is ticked,
 * where they were: the screen, the address, the browser and the app version.
 *
 * After a problem report, "Add details to TC-1042" puts more on the same
 * ticket, which is what "You can add more details here any time" promises.
 */

export const CREATE_SUPPORT_TICKET = gql`
  mutation CreateSupportTicket(
    $accountId: ID
    $kind: String!
    $message: String!
    $context: SupportTicketContext
  ) {
    createSupportTicket(accountId: $accountId, kind: $kind, message: $message, context: $context) {
      id
      ref
      kind
      status
    }
  }
`;

export const ADD_SUPPORT_TICKET_MESSAGE = gql`
  mutation AddSupportTicketMessage(
    $ticketId: ID!
    $message: String!
    $context: SupportTicketContext
  ) {
    addSupportTicketMessage(ticketId: $ticketId, message: $message, context: $context) {
      id
      ref
      status
    }
  }
`;

type Mode = 'feedback' | 'problem' | 'more';
type Msg = { from: 'us' | 'me'; text: string; meta?: string };
type Ticket = { id: string; ref: string };

const APP_VERSION = (process.env.REACT_APP_VERSION || 'dev').slice(0, 12);

const us = (text: string): Msg => ({ from: 'us', text });
const me = (text: string, meta?: string): Msg => ({ from: 'me', text, meta });

const ASK: Record<Mode, string> = {
  feedback: 'We’d love to hear it. What’s working, what isn’t, or what do you wish tinycms did?',
  problem: 'Sorry about that. What happened, and what did you expect to happen?',
  more: 'What else should we know?',
};
const LABEL: Record<Mode, string> = {
  feedback: 'Your feedback',
  problem: 'What happened?',
  more: 'More details',
};

/** The "?" in the top bar and the panel it opens. */
export function HelpFeedback({ email }: { email: string }) {
  const location = useLocation();
  const { accountId } = useAccount();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const [withContext, setWithContext] = useState(true);
  const [error, setError] = useState('');
  const [lastProblem, setLastProblem] = useState<Ticket | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [createTicket, { loading: creating }] = useMutation(CREATE_SUPPORT_TICKET);
  const [addMessage, { loading: adding }] = useMutation(ADD_SUPPORT_TICKET_MESSAGE);
  const sending = creating || adding;

  const name = nameFromEmail(email);
  const intro = us(
    `Hi${name ? ` ${name}` : ''}. Thanks for submitting your feedback/comments. We aim to reply within 24 hours.\n\nHow can we help?`
  );
  const shown = msgs.length ? msgs : [intro];
  const screen = screenName(location.pathname, location.search);
  const browser = browserOf(typeof navigator === 'undefined' ? '' : navigator.userAgent);
  const contextText = `${screen} · ${browser.name}`;
  const asksContext = mode === 'problem' || mode === 'more';

  // The newest message stays in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, mode, open, error]);

  // Opening moves focus into the panel; Escape closes it and returns focus.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);
  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  const pick = (next: Mode) => {
    setMsgs([
      ...shown,
      me(
        next === 'feedback'
          ? 'Share feedback'
          : next === 'problem'
            ? 'Report a problem'
            : `Add details to ${lastProblem?.ref}`
      ),
      us(ASK[next]),
    ]);
    setMode(next);
    setText('');
    setWithContext(true);
    setError('');
  };

  const restart = () => {
    setMsgs([]);
    setMode(null);
    setText('');
    setError('');
    setLastProblem(null);
  };

  const send = async () => {
    const message = text.trim();
    if (!message || !mode || sending) return;
    const context =
      asksContext && withContext
        ? {
            screen,
            url: window.location.href,
            browser: browser.full,
            appVersion: APP_VERSION,
          }
        : null;
    setError('');
    try {
      let ticket: Ticket;
      if (mode === 'more' && lastProblem) {
        const { data } = await addMessage({
          variables: { ticketId: lastProblem.id, message, context },
        });
        ticket = data.addSupportTicketMessage;
      } else {
        const { data } = await createTicket({
          variables: { accountId: accountId || null, kind: mode, message, context },
        });
        ticket = data.createSupportTicket;
      }
      const sent = me(message, context ? `Sent with: ${contextText}` : 'Sent');
      const reply =
        mode === 'feedback'
          ? `Thank you, ${name}. We really value your feedback, and a person on our team reads every message. We’ll get back to you at ${email} within 24 hours.`
          : mode === 'problem'
            ? `Thanks, ${name}. Your report is ${ticket.ref}. A person on our support team will look into it and reply to ${email} within 24 hours. You can add more details here any time.`
            : `Thanks, ${name}. We’ve added that to ${ticket.ref}.`;
      if (mode === 'problem') setLastProblem({ id: ticket.id, ref: ticket.ref });
      setMsgs([...shown, sent, us(reply), us('Anything else?')]);
      setMode(null);
      setText('');
    } catch (err) {
      const why = (err as { graphQLErrors?: Array<{ message?: string }> })?.graphQLErrors?.[0]
        ?.message;
      setError(why || 'That didn’t send. Check your connection and try again.');
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="ink-icon-btn"
        aria-label="Help and feedback"
        aria-expanded={open}
        title="Help and feedback"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ink-hf-glyph" aria-hidden="true">
          ?
        </span>
      </button>
      {/* On the body, so the header's stacking never puts a screen's own layers over it. */}
      {open
        ? createPortal(
            <div
              ref={panelRef}
              className="ink-hf"
              role="dialog"
              aria-label="Help and feedback"
              tabIndex={-1}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  close();
                }
              }}
            >
              <div className="ink-hf-head">
                <div className="ink-hf-title">Help &amp; feedback</div>
                {msgs.length ? (
                  <button
                    type="button"
                    className="ink-hf-head-btn"
                    onClick={restart}
                    aria-label="Start over"
                    title="Start over"
                  >
                    ↺
                  </button>
                ) : null}
                <button
                  type="button"
                  className="ink-hf-head-btn ink-hf-close"
                  onClick={close}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="ink-hf-log" ref={scrollRef} aria-live="polite">
                {shown.map((m, i) => (
                  <div key={i} className="ink-hf-msg" data-from={m.from}>
                    {m.from === 'us' ? <span className="ink-hf-who">tinycms team</span> : null}
                    <div className="ink-hf-bubble">{m.text}</div>
                    {m.meta ? <span className="ink-hf-meta">{m.meta}</span> : null}
                  </div>
                ))}
                {mode ? null : (
                  <div className="ink-hf-choices">
                    <button type="button" onClick={() => pick('feedback')}>
                      Share feedback
                    </button>
                    <button type="button" onClick={() => pick('problem')}>
                      Report a problem
                    </button>
                    {lastProblem ? (
                      <button type="button" onClick={() => pick('more')}>
                        Add details to {lastProblem.ref}
                      </button>
                    ) : null}
                    <a href={HELP_CENTER} target="_blank" rel="noreferrer">
                      Browse help ↗
                    </a>
                  </div>
                )}
              </div>
              {mode ? (
                <div className="ink-hf-compose">
                  <label className="ink-hf-label">
                    {LABEL[mode]}
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          void send();
                        }
                      }}
                      placeholder={LABEL[mode]}
                      rows={3}
                      maxLength={4000}
                      autoFocus
                    />
                  </label>
                  {asksContext ? (
                    <label className="ink-hf-check">
                      <input
                        type="checkbox"
                        checked={withContext}
                        onChange={(e) => setWithContext(e.target.checked)}
                      />
                      <span>
                        Include what page I'm on and my browser ({contextText}), so you can find it
                        faster.
                      </span>
                    </label>
                  ) : null}
                  {error ? (
                    <div className="ink-hf-error" role="alert">
                      {error}
                    </div>
                  ) : null}
                  <div className="ink-hf-send-row">
                    <span className="ink-hf-replies">Replies go to {email}</span>
                    <button
                      type="button"
                      className="ink-hf-send"
                      disabled={!text.trim() || sending}
                      onClick={() => void send()}
                    >
                      {sending ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
